// assets/price-slider.js
const GX_PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

function gxNumber(amount, decimals, thousands, decimalMark) {
  const fixed = Math.abs(amount).toFixed(decimals);
  const [int, frac] = fixed.split('.');
  const grouped = int.replace(/(\d)(?=(\d{3})+$)/g, `$1${thousands}`);
  return frac ? `${grouped}${decimalMark}${frac}` : grouped;
}

function gxFormatMoney(amount, format, noDecimals = true) {
  if (!format) return String(Math.round(amount));
  const match = format.match(GX_PLACEHOLDER);
  if (!match) return String(Math.round(amount));

  const commaDecimal =
    match[1] === 'amount_with_comma_separator' ||
    match[1] === 'amount_no_decimals_with_comma_separator';

  const decimals = noDecimals || match[1].includes('no_decimals') ? 0 : 2;
  const value = commaDecimal
    ? gxNumber(amount, decimals, '.', ',')
    : gxNumber(amount, decimals, ',', '.');

  return format.replace(GX_PLACEHOLDER, value);
}

class GlixorPriceSlider extends HTMLElement {
  connectedCallback() {
    if (this.gxReady) return;

    this.rangeMin  = this.querySelector('[data-gx-range-min]');
    this.rangeMax  = this.querySelector('[data-gx-range-max]');
    this.nativeMin = this.querySelector('[data-gx-native-min]');
    this.nativeMax = this.querySelector('[data-gx-native-max]');
    this.outMin    = this.querySelector('[data-gx-out-min]');
    this.outMax    = this.querySelector('[data-gx-out-max]');
    this.maxLabel  = this.querySelector('[data-gx-max-label]');
    this.track     = this.querySelector('[data-gx-track]');

    if (!this.rangeMin || !this.rangeMax || !this.nativeMin || !this.nativeMax) return;

    this.moneyFormat = this.dataset.moneyFormat || '';
    this.gteParam = this.nativeMin.name;
    this.lteParam = this.nativeMax.name;
    this.floor = 0;
    this.gap = 1;
    this.suppress = false;

    this.ceil = this.resolveCeil();

    [this.rangeMin, this.rangeMax].forEach((el) => {
      el.min = this.floor;
      el.max = this.ceil;
      el.setAttribute('aria-valuemin', this.floor);
      el.setAttribute('aria-valuemax', this.ceil);
    });

    // Native max attr must match the real ceiling, or number-input
    // validation silently rejects a valid lte value.
    this.nativeMin.max = this.ceil;
    this.nativeMax.max = this.ceil;
    this.nativeMax.placeholder = this.ceil;

    if (this.maxLabel) {
      this.maxLabel.textContent = gxFormatMoney(this.ceil, this.moneyFormat, false);
    }

    this.onSlide   = this.onSlide.bind(this);
    this.onCommit  = this.onCommit.bind(this);
    this.onNative  = this.onNative.bind(this);
    this.onPointer = this.onPointer.bind(this);

    this.rangeMin.addEventListener('input', this.onSlide);
    this.rangeMax.addEventListener('input', this.onSlide);
    this.rangeMin.addEventListener('change', this.onCommit);
    this.rangeMax.addEventListener('change', this.onCommit);
    this.nativeMin.addEventListener('change', this.onNative);
    this.nativeMax.addEventListener('change', this.onNative);
    this.track.addEventListener('pointerdown', this.onPointer, true);

    this.syncFromNative();
    this.gxReady = true;
  }

  disconnectedCallback() {
    this.rangeMin?.removeEventListener('input', this.onSlide);
    this.rangeMax?.removeEventListener('input', this.onSlide);
    this.rangeMin?.removeEventListener('change', this.onCommit);
    this.rangeMax?.removeEventListener('change', this.onCommit);
    this.nativeMin?.removeEventListener('change', this.onNative);
    this.nativeMax?.removeEventListener('change', this.onNative);
    this.track?.removeEventListener('pointerdown', this.onPointer, true);
  }

  /**
   * filter.range_max shrinks as filters narrow the result set, which would
   * ratchet the ceiling down to zero. Take the highest value ever observed
   * for this collection instead.
   */
  resolveCeil() {
    const rendered = Math.ceil(parseFloat(this.nativeMax.getAttribute('max')) || 0);
    const key = `gx-price-ceil:${window.location.pathname}`;
    const params = new URLSearchParams(window.location.search);
    const priceActive = params.has(this.gteParam) || params.has(this.lteParam);

    let remembered = 0;
    try {
      remembered = parseFloat(window.sessionStorage.getItem(key)) || 0;
    } catch (e) { /* private mode */ }

    // Only a price-unfiltered render reports the true collection ceiling.
    if (!priceActive && rendered > remembered) {
      remembered = rendered;
      try { window.sessionStorage.setItem(key, String(rendered)); } catch (e) {}
    }

    const applied = Math.ceil(parseFloat(params.get(this.lteParam)) || 0);
    return Math.max(rendered, remembered, applied, 1);
  }

  clamp(n) {
    return Math.min(this.ceil, Math.max(this.floor, n));
  }

  toNum(raw, fallback) {
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : fallback;
  }

  syncFromNative() {
    const min = this.clamp(Math.floor(this.toNum(this.nativeMin.value, this.floor)));
    const max = this.clamp(
      Math.ceil(this.nativeMax.value === '' ? this.ceil : this.toNum(this.nativeMax.value, this.ceil))
    );
    this.rangeMin.value = Math.min(min, max);
    this.rangeMax.value = Math.max(max, min);
    this.render();
  }

  /**
   * Both range inputs overlap. Raise whichever handle is nearer the pointer
   * so a stacked pair never traps the one underneath.
   */
  onPointer(event) {
    const rect = this.track.getBoundingClientRect();
    if (!rect.width) return;
    const pct = ((event.clientX - rect.left) / rect.width) * 100;
    const span = this.ceil - this.floor || 1;
    const lo = ((this.toNum(this.rangeMin.value, this.floor) - this.floor) / span) * 100;
    const hi = ((this.toNum(this.rangeMax.value, this.ceil) - this.floor) / span) * 100;

    const minIsNearer = Math.abs(pct - lo) <= Math.abs(pct - hi);
    this.rangeMin.style.zIndex = minIsNearer ? 5 : 3;
    this.rangeMax.style.zIndex = minIsNearer ? 3 : 5;
  }

  onSlide(event) {
    let min = this.clamp(this.toNum(this.rangeMin.value, this.floor));
    let max = this.clamp(this.toNum(this.rangeMax.value, this.ceil));

    if (max - min < this.gap) {
      if (event.target === this.rangeMin) {
        min = Math.max(this.floor, max - this.gap);
        this.rangeMin.value = min;
      } else {
        max = Math.min(this.ceil, min + this.gap);
        this.rangeMax.value = max;
      }
    }
    this.render();
  }

  onCommit() {
    const min = this.toNum(this.rangeMin.value, this.floor);
    const max = this.toNum(this.rangeMax.value, this.ceil);

    const nextMin = min <= this.floor ? '' : String(min);
    const nextMax = max >= this.ceil ? '' : String(max);

    if (this.nativeMin.value === nextMin && this.nativeMax.value === nextMax) return;

    // Guard against our own dispatched events re-entering onNative.
    this.suppress = true;
    this.nativeMin.value = nextMin;
    this.nativeMax.value = nextMax;

    [this.nativeMin, this.nativeMax].forEach((el) => {
      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.dispatchEvent(new Event('change', { bubbles: true }));
    });
    this.suppress = false;
  }

  onNative() {
    if (this.suppress) return;
    this.syncFromNative();
  }

 render() {
    const min = this.toNum(this.rangeMin.value, this.floor);
    const max = this.toNum(this.rangeMax.value, this.ceil);
    const span = this.ceil - this.floor || 1;

    // Unitless 0–1: CSS calc() maps these onto the native thumb travel path.
    this.track.style.setProperty('--gx-lo-n', (min - this.floor) / span);
    this.track.style.setProperty('--gx-hi-n', (max - this.floor) / span);

    this.rangeMin.setAttribute('aria-valuenow', min);
    this.rangeMax.setAttribute('aria-valuenow', max);

    if (this.outMin) this.outMin.textContent = gxFormatMoney(min, this.moneyFormat);
    if (this.outMax) this.outMax.textContent = gxFormatMoney(max, this.moneyFormat);
  }
}

if (!customElements.get('glixor-price-slider')) {
  customElements.define('glixor-price-slider', GlixorPriceSlider);
}